import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/network/http';
import type { NotificationListResponse, UnreadCountResponse } from '../types';

/**
 * Fetch user's notifications with pagination
 */
export const useNotifications = (params?: { isRead?: boolean; page?: number; limit?: number }) => {
  return useQuery<NotificationListResponse>({
    queryKey: ['notifications', params],
    queryFn: async () => {
      const { data } = await http.get('/notifications', { params });
      return data;
    },
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Infinite-scroll hook — loads 10 notifications per page
 */
export const useInfiniteNotifications = () => {
  return useInfiniteQuery<NotificationListResponse>({
    queryKey: ['notifications', 'infinite'],
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await http.get('/notifications', {
        params: { page: pageParam, limit: 10 },
      });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Unread notification count — polls every 2 minutes
 */
export const useUnreadCount = () => {
  return useQuery<UnreadCountResponse>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const { data } = await http.get('/notifications/unread-count');
      return data;
    },
    refetchInterval: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
    staleTime: 1 * 60 * 1000,
  });
};

/**
 * Mark a single notification as read — with optimistic cache update
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { data } = await http.patch(`/notifications/${notificationId}/read`);
      return data;
    },
    onMutate: async (notificationId: string) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });

      const prevInfinite = queryClient.getQueryData<{ pages: NotificationListResponse[]; pageParams: unknown[] }>(['notifications', 'infinite']);
      const prevCount = queryClient.getQueryData<UnreadCountResponse>(['notifications', 'unread-count']);

      // Optimistically mark as read in infinite list
      if (prevInfinite) {
        queryClient.setQueryData<typeof prevInfinite>(['notifications', 'infinite'], {
          ...prevInfinite,
          pages: prevInfinite.pages.map((page) => ({
            ...page,
            data: page.data.map((n) =>
              n.id === notificationId ? { ...n, isRead: true } : n
            ),
          })),
        });
      }

      // Optimistically decrement unread count
      if (prevCount) {
        queryClient.setQueryData<UnreadCountResponse>(['notifications', 'unread-count'], {
          ...prevCount,
          data: { count: Math.max(0, prevCount.data.count - 1) },
        });
      }

      return { prevInfinite, prevCount };
    },
    onError: (_err, _id, context) => {
      if (context?.prevInfinite) {
        queryClient.setQueryData(['notifications', 'infinite'], context.prevInfinite);
      }
      if (context?.prevCount) {
        queryClient.setQueryData(['notifications', 'unread-count'], context.prevCount);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

/**
 * Mark all notifications as read
 */
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await http.patch('/notifications/mark-all-read');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

/**
 * Delete a single notification
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { data } = await http.delete(`/notifications/${notificationId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

/**
 * Delete all notifications
 */
export const useDeleteAllNotifications = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await http.delete('/notifications/all');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};
